import axios from 'axios';

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'development' ? '/api' : 'https://backend.tripiigo.com/api');

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url || '';
    if (status === 401 && !url.includes('/auth/login') && !url.includes('/auth/register')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
    return Promise.reject(error);
  }
);

const crudAPI = (path: string) => ({
  getAll: (params?: Record<string, string>) => api.get(path, { params }),
  getById: (id: string) => api.get(`${path}/${id}`),
  create: (data: Record<string, unknown>) => api.post(path, data),
  update: (id: string, data: Record<string, unknown>) => api.put(`${path}/${id}`, data),
  remove: (id: string) => api.delete(`${path}/${id}`),
  export: () => api.get(`${path}/export`, { responseType: 'blob' }),
  import: (records: Record<string, unknown>[]) => api.post(`${path}/import`, { records }),
});

export const authAPI = {
  register: (data: Record<string, unknown>) => api.post('/auth/register', data),
  login: (email: string, password: string) => api.post('/auth/login', { email, password }),
  getCurrentUser: () => api.get('/auth/me'),
  updateProfile: (data: Record<string, unknown>) => api.put('/auth/profile', data),
  getPublicPlans: () => api.get('/auth/plans'),
  forgotPassword: (email: string) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token: string, password: string) => api.post('/auth/reset-password', { token, password }),
};

export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (data: Record<string, unknown>) => api.put('/settings', data),
  uploadBranding: (formData: FormData) =>
    api.post('/settings/branding/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

export const localeAPI = {
  getConfig: () => api.get('/locale/config'),
  getMyLocale: () => api.get('/locale/me'),
};

export const superAdminAPI = {
  getDashboard: (params?: Record<string, string>) => api.get('/super-admin/dashboard', { params }),
  getTenants: () => api.get('/super-admin/tenants'),
  updateTenant: (id: string, data: Record<string, unknown>) => api.put(`/super-admin/tenants/${id}`, data),
  assignPlan: (id: string, subscriptionPlanId: string, options?: Record<string, unknown>) =>
    api.post(`/super-admin/tenants/${id}/assign-plan`, { subscriptionPlanId, ...options }),
  updateTenantStatus: (id: string, status: string) =>
    api.patch(`/super-admin/tenants/${id}/status`, { status }),
  getTenantModuleAccess: (tenantId: string) => api.get(`/super-admin/tenants/${tenantId}/modules`),
  updateTenantModuleAccess: (tenantId: string, modules: Array<{ moduleKey: string; enabled: boolean }>) =>
    api.put(`/super-admin/tenants/${tenantId}/modules`, { modules }),
  getPlans: () => api.get('/super-admin/plans'),
  createPlan: (data: Record<string, unknown>) => api.post('/super-admin/plans', data),
  updatePlan: (id: string, data: Record<string, unknown>) => api.put(`/super-admin/plans/${id}`, data),
  deletePlan: (id: string) => api.delete(`/super-admin/plans/${id}`),
  getPendingTenants: () => api.get('/super-admin/tenants/pending'),
  getTenantUsage: (id: string) => api.get(`/super-admin/tenants/${id}/usage`),
  getAnalytics: () => api.get('/super-admin/analytics'),
  getPlatformSettings: () => api.get('/super-admin/platform/settings'),
  updatePlatformSettings: (data: Record<string, unknown>) => api.put('/super-admin/platform/settings', data),
  getFeatureFlags: () => api.get('/super-admin/platform/feature-flags'),
  updateFeatureFlags: (data: Record<string, unknown>) => api.put('/super-admin/platform/feature-flags', data),
  getCoreModules: () => api.get('/super-admin/platform/core-modules'),
  updateCoreModules: (data: Record<string, unknown>) => api.put('/super-admin/platform/core-modules', data),
  getFieldTypes: () => api.get('/super-admin/platform/field-types'),
  updateFieldTypes: (data: Record<string, unknown>) => api.put('/super-admin/platform/field-types', data),
  getDefaultFields: () => api.get('/super-admin/platform/default-fields'),
  updateDefaultFields: (data: Record<string, unknown>) => api.put('/super-admin/platform/default-fields', data),
  getRelationshipRules: () => api.get('/super-admin/platform/relationship-rules'),
  createRelationshipRule: (data: Record<string, unknown>) => api.post('/super-admin/platform/relationship-rules', data),
  updateRelationshipRule: (id: string, data: Record<string, unknown>) => api.put(`/super-admin/platform/relationship-rules/${id}`, data),
  deleteRelationshipRule: (id: string) => api.delete(`/super-admin/platform/relationship-rules/${id}`),
  getMasterData: (category: string) => api.get(`/super-admin/platform/master-data/${category}`),
  createMasterData: (category: string, data: Record<string, unknown>) => api.post(`/super-admin/platform/master-data/${category}`, data),
  updateMasterData: (category: string, id: string, data: Record<string, unknown>) => api.put(`/super-admin/platform/master-data/${category}/${id}`, data),
  deleteMasterData: (category: string, id: string) => api.delete(`/super-admin/platform/master-data/${category}/${id}`),
  getSuperAdminUsers: () => api.get('/super-admin/users'),
  getLoginLogs: () => api.get('/super-admin/security/login-logs'),
  getTransactions: (params?: Record<string, string>) => api.get('/super-admin/transactions', { params }),
  getTransaction: (id: string) => api.get(`/super-admin/transactions/${id}`),
  getSubscriptions: (params?: Record<string, string>) => api.get('/super-admin/subscriptions', { params }),
  upsertSubscription: (data: Record<string, unknown>) => api.post('/super-admin/subscriptions', data),
  getBillingInvoices: (params?: Record<string, string>) => api.get('/super-admin/invoices', { params }),
  createBillingInvoice: (data: Record<string, unknown>) => api.post('/super-admin/invoices', data),
  getBillingInvoice: (id: string) => api.get(`/super-admin/invoices/${id}`),
  updateBillingInvoice: (id: string, data: Record<string, unknown>) => api.put(`/super-admin/invoices/${id}`, data),
  updateBillingInvoiceStatus: (id: string, status: string, paymentDate?: string, paymentMode?: string) =>
    api.put(`/super-admin/invoices/${id}/status`, { status, paymentDate, paymentMode }),
  checkBillingInvoiceNumber: (number: string) => api.get(`/super-admin/invoices/check/${encodeURIComponent(number)}`),
  runBillingJob: () => api.post('/super-admin/billing/run-job'),
  getBillingProfile: () => api.get('/super-admin/platform/billing'),
  updateBillingProfile: (data: Record<string, unknown>) => api.put('/super-admin/platform/billing', data),
  uploadBillingAsset: (formData: FormData) =>
    api.post('/super-admin/platform/billing/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  sendInvoiceEmail: (id: string, data?: Record<string, unknown>) =>
    api.post(`/super-admin/invoices/${id}/send-email`, data || {}),
  getIntegrationMarketplace: () => api.get('/super-admin/platform/integrations/marketplace'),
  updateIntegrationMarketplace: (data: Record<string, unknown>) =>
    api.put('/super-admin/platform/integrations/marketplace', data),
  getCrmModules: () => api.get('/super-admin/crm-modules'),
  createCrmModule: (data: Record<string, unknown>) => api.post('/super-admin/crm-modules', data),
  updateCrmModule: (key: string, data: Record<string, unknown>) => api.put(`/super-admin/crm-modules/${key}`, data),
  deleteCrmModule: (key: string) => api.delete(`/super-admin/crm-modules/${key}`),
  getReportTemplates: () => api.get('/super-admin/report-templates'),
  createReportTemplate: (data: Record<string, unknown>) => api.post('/super-admin/report-templates', data),
  updateReportTemplate: (key: string, data: Record<string, unknown>) => api.put(`/super-admin/report-templates/${key}`, data),
  deleteReportTemplate: (key: string) => api.delete(`/super-admin/report-templates/${key}`),
  getTenantReportAccess: (tenantId: string) => api.get(`/super-admin/tenants/${tenantId}/reports`),
  updateTenantReportAccess: (tenantId: string, reports: Array<{ reportKey: string; enabled: boolean }>) =>
    api.put(`/super-admin/tenants/${tenantId}/reports`, { reports }),
};

export const companyAPI = crudAPI('/companies');
export const branchAPI = crudAPI('/branches');
export const clientAPI = crudAPI('/clients');
export const consignorAPI = crudAPI('/consignors');
export const consigneeAPI = crudAPI('/consignees');
export const staffAPI = crudAPI('/staff');
export const driverAPI = crudAPI('/drivers');
export const helperAPI = crudAPI('/helpers');
export const vehicleVendorAPI = crudAPI('/vehicle-vendors');
export const vehicleAPI = {
  ...crudAPI('/vehicles'),
  systemTotal: () => api.get('/vehicles/system-total'),
};

export const statsAPI = {
  systemTotal: (moduleKey: string) => api.get(`/stats/system-total/${encodeURIComponent(moduleKey)}`),
};
export const tripAPI = {
  ...crudAPI('/trips'),
  assign: (id: string, data: Record<string, unknown>) => api.post(`/trips/${id}/assign`, data),
  start: (id: string) => api.post(`/trips/${id}/start`),
  complete: (id: string) => api.post(`/trips/${id}/complete`),
  approve: (id: string) => api.post(`/trips/${id}/approve`),
  reachStop: (id: string, sequence: number) => api.post(`/trips/${id}/stops/${sequence}/reach`),
  createBilty: (id: string) => api.post(`/trips/${id}/create-bilty`),
  fromLead: (leadId: string, data: Record<string, unknown>) => api.post(`/trips/from-lead/${leadId}`, data),
  notifyWhatsApp: (id: string) => api.post(`/trips/${id}/whatsapp/notify`),
};

export const masterDataAPI = {
  getTree: () => api.get('/master-data/tree'),
  getCategory: (category: string, params?: Record<string, string>) => api.get(`/master-data/${category}`, { params }),
};

export const invoiceAPI = {
  ...crudAPI('/invoices'),
  createFromBilty: (biltyId: string, data?: Record<string, unknown>) => api.post(`/invoices/from-bilty/${biltyId}`, data || {}),
};
export const fuelAPI = {
  ...crudAPI('/fuel'),
  getEmployees: () => api.get('/fuel/employees'),
};
export const maintenanceAPI = crudAPI('/maintenance');
export const assetAPI = {
  upload: (formData: FormData) =>
    api.post('/assets/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};
export const biltyAPI = {
  getAll: (params?: Record<string, string>) => api.get('/bilty', { params }),
  getById: (id: string) => api.get(`/bilty/${id}`),
  create: (data: Record<string, unknown>) => api.post('/bilty', data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/bilty/${id}`, data),
  remove: (id: string) => api.delete(`/bilty/${id}`),
  getNextNumber: (date?: string) => api.get('/bilty/next-number', { params: date ? { date } : {} }),
  checkNumber: (number: string, excludeId?: string) =>
    api.get(`/bilty/check/${encodeURIComponent(number)}`, { params: excludeId ? { excludeId } : {} }),
  export: () => api.get('/bilty/export', { responseType: 'blob' }),
  import: (records: Record<string, unknown>[]) => api.post('/bilty/import', { records }),
};
export const biltyItemDescriptionAPI = {
  getAll: (params?: Record<string, string>) => api.get('/bilty/descriptions', { params }),
  create: (data: Record<string, unknown>) => api.post('/bilty/descriptions', data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/bilty/descriptions/${id}`, data),
  remove: (id: string) => api.delete(`/bilty/descriptions/${id}`),
};
export const roleAPI = crudAPI('/roles');

export const userAPI = {
  getAll: () => api.get('/users'),
  getById: (id: string) => api.get(`/users/${id}`),
  create: (data: Record<string, unknown>) => api.post('/users', data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/users/${id}`, data),
  remove: (id: string) => api.delete(`/users/${id}`),
  resetPassword: (id: string, password: string) => api.post(`/users/${id}/reset-password`, { password }),
};

export const moduleAPI = {
  getAll: () => api.get('/modules'),
  getFieldTypes: () => api.get('/modules/field-types'),
  getSidebar: () => api.get('/modules/sidebar'),
  getConfig: () => api.get('/modules/config'),
  updateConfig: (data: Record<string, unknown>) => api.put('/modules/config', data),
  create: (data: Record<string, unknown>) => api.post('/modules', data),
  update: (key: string, data: Record<string, unknown>) => api.put(`/modules/${key}`, data),
  remove: (key: string) => api.delete(`/modules/${key}`),
  getFields: (key: string) => api.get(`/modules/${key}/fields`),
  addField: (key: string, data: Record<string, unknown>) => api.post(`/modules/${key}/fields`, data),
  updateField: (key: string, fieldKey: string, data: Record<string, unknown>) =>
    api.put(`/modules/${key}/fields/${fieldKey}`, data),
  setFieldState: (key: string, fieldKey: string, fieldState: string) =>
    api.patch(`/modules/${key}/fields/${fieldKey}/state`, { fieldState }),
};

export const activityAPI = {
  getStream: (moduleKey: string, recordId: string) => api.get(`/activity/${moduleKey}/${recordId}`),
  addComment: (moduleKey: string, recordId: string, comment: string, mentions?: string[]) =>
    api.post(`/activity/${moduleKey}/${recordId}/comment`, { comment, mentions }),
  getFeed: (params?: { limit?: number; moduleKey?: string }) =>
    api.get('/activity/feed', { params }),
};

export const teamAPI = {
  list: () => api.get('/teams'),
  create: (data: Record<string, unknown>) => api.post('/teams', data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/teams/${id}`, data),
  remove: (id: string) => api.delete(`/teams/${id}`),
  members: () => api.get('/teams/members'),
};

export const assignmentAPI = {
  get: (moduleKey: string, recordId: string) => api.get(`/assignments/${moduleKey}/${recordId}`),
  assign: (moduleKey: string, recordId: string, data: Record<string, unknown>) =>
    api.post(`/assignments/${moduleKey}/${recordId}`, data),
  myAssignments: () => api.get('/assignments/my'),
  assignableUsers: () => api.get('/assignments/assignable-users'),
};

export const filterAPI = {
  list: (moduleKey: string) => api.get(`/filters/${moduleKey}`),
  save: (moduleKey: string, data: Record<string, unknown>, id?: string) =>
    id ? api.put(`/filters/${moduleKey}/${id}`, data) : api.post(`/filters/${moduleKey}`, data),
  remove: (moduleKey: string, id: string) => api.delete(`/filters/${moduleKey}/${id}`),
  search: (moduleKey: string, body: Record<string, unknown>) => api.post(`/filters/${moduleKey}/search`, body),
};

export const crmActivityAPI = {
  getSummary: () => api.get('/crm-activities/summary'),
  sync: (providers?: string[]) => api.post('/crm-activities/sync', providers ? { providers } : {}),
  getLinked: (opts: { relatedLead?: string; leadId?: string }) =>
    api.get('/crm-activities/linked', { params: opts }),
};

export const integrationAPI = {
  list: () => api.get('/integrations'),
  update: (provider: string, data: Record<string, unknown>) => api.put(`/integrations/${provider}`, data),
  testConnection: (provider: string) => api.post(`/integrations/${provider}/test`),
  syncShopify: () => api.post('/integrations/shopify/sync'),
};

export const notificationAPI = {
  list: (params?: { limit?: number; unreadOnly?: boolean }) =>
    api.get('/notifications', { params: params ? { limit: params.limit, unreadOnly: params.unreadOnly } : undefined }),
  unreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id: string) => api.patch(`/notifications/${id}/read`),
  markAllRead: () => api.patch('/notifications/read-all'),
  remove: (id: string) => api.delete(`/notifications/${id}`),
};

export const recordAPI = {
  list: (moduleKey: string, params?: Record<string, string>) => api.get(`/records/${moduleKey}`, { params }),
  get: (moduleKey: string, id: string) => api.get(`/records/${moduleKey}/${id}`),
  create: (moduleKey: string, data: Record<string, unknown>) => api.post(`/records/${moduleKey}`, data),
  update: (moduleKey: string, id: string, data: Record<string, unknown>) => api.put(`/records/${moduleKey}/${id}`, data),
  remove: (moduleKey: string, id: string) => api.delete(`/records/${moduleKey}/${id}`),
  export: (moduleKey: string) => api.get(`/records/${moduleKey}/export`, { responseType: 'blob' }),
  import: (moduleKey: string, records: Record<string, unknown>[]) => api.post(`/records/${moduleKey}/import`, { records }),
};

export const layoutAPI = {
  get: (moduleKey: string, viewType: string) => api.get(`/layouts/${moduleKey}/${viewType}`),
  save: (moduleKey: string, viewType: string, data: Record<string, unknown>) => api.put(`/layouts/${moduleKey}/${viewType}`, data),
};

export const searchAPI = {
  search: (q: string, module?: string) => api.get('/search', { params: { q, module } }),
};

export const reportAPI = {
  list: () => api.get('/reports'),
  filters: (params?: Record<string, string>) => api.get('/reports/filters', { params }),
  overview: (params?: Record<string, string>) => api.get('/reports/overview', { params }),
  dashboard: (params?: Record<string, string>) => api.get('/reports/dashboard', { params }),
  create: (data: Record<string, unknown>) => api.post('/reports', data),
  run: (id: string, params?: Record<string, string>) =>
    api.post(`/reports/${encodeURIComponent(id)}/run`, {}, { params }).catch((err) => {
      if (err?.response?.status === 404 || err?.response?.status === 405) {
        return api.get(`/reports/${encodeURIComponent(id)}/run`, { params });
      }
      throw err;
    }),
  remove: (id: string) => api.delete(`/reports/${id}`),
};

export const fleetReportAPI = {
  monthlyVehicles: (params?: Record<string, string>) => api.get('/fleet-reports/monthly/vehicles', { params }),
  monthlyTrips: (params?: Record<string, string>) => api.get('/fleet-reports/monthly/trips', { params }),
  monthlyAccounts: (params?: Record<string, string>) => api.get('/fleet-reports/monthly/accounts', { params }),
};

export const cashMemoAPI = crudAPI('/cash-memo');
export const fastagAPI = crudAPI('/fastag');
export const batteryMaintenanceAPI = crudAPI('/battery-maintenance');

export const documentAPI = {
  getTypes: () => api.get('/documents/types'),
  previewNextMemo: () => api.get('/documents/next-memo'),
  list: (params?: Record<string, string>) => api.get('/documents', { params }),
  getExpiring: (days = 30) => api.get('/documents/expiring', { params: { days: String(days) } }),
  getVehicleCompliance: (params?: Record<string, string>) =>
    api.get('/documents/vehicle-compliance', { params }),
  getById: (id: string) => api.get(`/documents/${id}`),
  upload: (formData: FormData) =>
    api.post('/documents/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id: string, formData: FormData) =>
    api.put(`/documents/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  verify: (id: string, data: { verificationStatus: string; remarks?: string }) =>
    api.put(`/documents/${id}/verify`, data),
  remove: (id: string) => api.delete(`/documents/${id}`),
  downloadFile: async (id: string) => {
    const res = await api.get(`/documents/${id}/file`, { responseType: 'blob' });
    return res.data as Blob;
  },
};

export const gpsAPI = {
  getProviders: () => api.get('/gps/providers'),
  getLive: (params?: Record<string, string>) => api.get('/gps/live', { params }),
  getDevices: (params?: Record<string, string>) => api.get('/gps/devices', { params }),
  getDevice: (id: string) => api.get(`/gps/devices/${id}`),
  createDevice: (data: Record<string, unknown>) => api.post('/gps/devices', data),
  updateDevice: (id: string, data: Record<string, unknown>) => api.put(`/gps/devices/${id}`, data),
  deleteDevice: (id: string) => api.delete(`/gps/devices/${id}`),
  simulateLocation: (deviceId: string, data: Record<string, unknown>) =>
    api.post(`/gps/devices/${deviceId}/simulate`, data),
  getVehicleLocation: (vehicleId: string) => api.get(`/gps/vehicles/${vehicleId}/location`),
  getLocationHistory: (vehicleId: string, params?: Record<string, string>) =>
    api.get(`/gps/vehicles/${vehicleId}/history`, { params }),
  getVehicleTrips: (vehicleId: string, params?: Record<string, string>) =>
    api.get(`/gps/vehicles/${vehicleId}/trips`, { params }),
  getTripPlayback: (tripId: string) => api.get(`/gps/trips/${tripId}/playback`),
  getClientVehicles: (clientId: string) => api.get(`/gps/clients/${clientId}/vehicles`),
  getAlerts: (params?: Record<string, string>) => api.get('/gps/alerts', { params }),
  acknowledgeAlert: (id: string) => api.patch(`/gps/alerts/${id}/acknowledge`),
  resolveAlert: (id: string) => api.patch(`/gps/alerts/${id}/resolve`),
  getGeofences: () => api.get('/gps/geofences'),
  createGeofence: (data: Record<string, unknown>) => api.post('/gps/geofences', data),
  updateGeofence: (id: string, data: Record<string, unknown>) => api.put(`/gps/geofences/${id}`, data),
  deleteGeofence: (id: string) => api.delete(`/gps/geofences/${id}`),
  getReportTypes: () => api.get('/gps/reports/types'),
  runReport: (reportType: string, params?: Record<string, string>) =>
    api.get(`/gps/reports/${reportType}`, { params }),
};

export default api;
